import React, { KeyboardEventHandler, useEffect, useState } from "react";
import { Textarea } from "@mui/joy";
import { isMobile } from "react-device-detect";
import emojiRegex from "emoji-regex";
import { theme } from "../../../../../styles/theme";

export default function PlainTextarea({
  placeholder,
  initialValue,
  handleChange,
  isFocused,
  setIsFocused,
  editorKey,
  emojiDisabled,
  readOnly,
  handleChangeWithoutDebounce,
}: {
  placeholder?: string;
  initialValue?: string;
  handleChange: (value: string, htmlString?: string) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  editorKey?: string;
  emojiDisabled?: boolean;
  readOnly?: boolean;
  handleChangeWithoutDebounce?: (value: string, htmlString?: string) => void;
}) {
  const [value, setValue] = useState(initialValue ?? "");
  const [isComposing, setIsComposing] = useState(false);

  // initialValue가 바뀌었을 때만, 그리고 포커스가 없을 때만 동기화
  useEffect(() => {
    if (!isFocused && initialValue !== undefined) {
      setValue(initialValue);
    }
  }, [initialValue, isFocused]);

  const emoji_regex = emojiRegex();
  // 공백(\s)은 허용. 필요 시 허용 문자 확장 가능
  const disabled_regex = /[^a-zA-Z0-9ㄱ-힣\s]/g;

  const sanitize = (text: string) => {
    if (!emojiDisabled) return text;
    // 필요 시 이모지/특수문자 제거. 조합 중에는 호출하지 않음
    let next = text.replace(disabled_regex, "");
    return next;
  };

  const flush = (text: string) => {
    handleChange(text, `<p>${text}</p>`);
  };

  const flushImmediately = (text: string) => {
    if (handleChangeWithoutDebounce) {
      handleChangeWithoutDebounce(text, `<p>${text}</p>`);
    } else {
      handleChange(text, `<p>${text}</p>`);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    const isEnter =
      e.code === "Enter" ||
      e.code === "NumpadEnter" ||
      e.key === "Enter" ||
      e.key === "NumpadEnter";

    if (!isMobile && isEnter) {
      // 조합 입력 중에는 Enter를 무시(조합 완료 대기)
      // 일부 브라우저는 nativeEvent.isComposing으로 노출
      if (isComposing || e.nativeEvent?.isComposing) {
        return;
      }
      // 다음 블록으로 넘어가기 전에 현재 값 즉시 플러시
      flushImmediately(value ?? "");
    }
  };

  return (
    <Textarea
      key={`${editorKey}`} // 가능하면 포커스 전환만으로 key가 바뀌지 않게 유지
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => {
        const raw = e.target.value;

        if (isComposing) {
          // 조합 중에는 가공하지 않고 그대로 반영하되,
          // 상위에도 즉시 전달하여 (Android 등) 진행 버튼이 나타나도록 함
          setValue(raw);
          flush(raw);
          return;
        }

        const next = sanitize(raw);
        setValue(next);
        flush(next);
      }}
      onCompositionStart={() => {
        setIsComposing(true);
      }}
      onCompositionEnd={(e) => {
        setIsComposing(false);
        const raw = (e.target as HTMLTextAreaElement).value;
        const next = sanitize(raw);
        setValue(next);
        flush(next);
      }}
      readOnly={readOnly}
      minRows={1}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={(e) => {
        // 값 먼저 플러시 → 이후 포커스 false
        const raw = (e.target as HTMLTextAreaElement).value;
        const next = isComposing ? raw : sanitize(raw);
        flushImmediately(next);
        setIsFocused(false);
      }}
      onKeyDown={handleKeyDown}
      sx={{
        padding: 0,
        minWidth: 0,
        minHeight: "24px",
        backgroundColor: "transparent",
        borderWidth: 0,
        boxShadow: "none",
        "--joy-focus-thickness": "0px",
        "--variant-borderWidth": "0px",
        "--Textarea-gap": "0px",
        "& textarea": {
          fontSize: "16px",
          fontWeight: 500,
        },
        "& textarea::placeholder": {
          color: theme.vars.palette.Content.SuperWeak,
        },
      }}
    />
  );
}